import { useState } from 'react';
import { AlertTriangle } from 'lucide-react';
import { Button, Modal } from '../../../ui';
import type { ProspectSignal } from '../../../services/prospect-signals.service';

interface PromoteSignalModalProps {
  signal: ProspectSignal | null;
  stages: Array<{ id: number; name: string }>;
  users: Array<{ id: number; name: string }>;
  isPending: boolean;
  onConfirm: (body: { stageId: number; assignedUserId: number }) => void;
  onClose: () => void;
}

const selectClass =
  'w-full rounded-md border border-border bg-surface px-3 py-2 text-sm ' +
  'focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent';

export function PromoteSignalModal({
  signal,
  stages,
  users,
  isPending,
  onConfirm,
  onClose,
}: PromoteSignalModalProps) {
  const [stageId, setStageId] = useState('');
  const [assignedUserId, setAssignedUserId] = useState('');

  if (!signal) {
    return null;
  }

  // O backend rejeita em 400 sem stageId E assignedUserId — nenhum dos dois
  // tem default lá de propósito. Bloquear aqui evita transformar decisão de
  // triagem em erro de formulário.
  const canConfirm = stageId !== '' && assignedUserId !== '' && !isPending;

  return (
    <Modal
      open
      onClose={onClose}
      title={`Promover @${signal.authorHandle}`}
      description="O sinal sai da triagem e passa a existir como Lead no pipeline."
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose} disabled={isPending}>
            Cancelar
          </Button>
          <Button
            disabled={!canConfirm}
            onClick={() =>
              onConfirm({ stageId: Number(stageId), assignedUserId: Number(assignedUserId) })
            }
          >
            Confirmar promoção
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-4">
        {/*
          ProspectSignal é deliberadamente separado de Lead e não tem e-mail nem
          telefone, por desenho: Lead está ligado a EmailCampaignRecipient. Este
          aviso torna visível a fronteira que está sendo atravessada.
        */}
        <div className="flex gap-2 rounded-md border border-warning bg-warning/10 p-3">
          <AlertTriangle className="w-4 h-4 shrink-0 text-warning-contrast" aria-hidden />
          <p className="text-xs text-ink-secondary">
            Promover <strong>cria um Lead real</strong> no pipeline, que passa a ser alcançável por
            campanhas de e-mail. Não há como desfazer pela tela.
          </p>
        </div>

        <div>
          <label
            htmlFor="promote-stage"
            className="mb-1 block text-sm font-medium text-ink-primary"
          >
            Etapa
          </label>
          <select
            id="promote-stage"
            className={selectClass}
            value={stageId}
            onChange={(e) => setStageId(e.target.value)}
            disabled={isPending}
          >
            <option value="">Selecione a etapa</option>
            {stages.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label htmlFor="promote-user" className="mb-1 block text-sm font-medium text-ink-primary">
            Responsável
          </label>
          <select
            id="promote-user"
            className={selectClass}
            value={assignedUserId}
            onChange={(e) => setAssignedUserId(e.target.value)}
            disabled={isPending}
          >
            <option value="">Selecione o responsável</option>
            {users.map((u) => (
              <option key={u.id} value={u.id}>
                {u.name}
              </option>
            ))}
          </select>
        </div>
      </div>
    </Modal>
  );
}
